import { CourierDto, KnownCourierApprovalStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { AddNoteActionModal } from '../../../ui/components/higher-order/add-note-action/add-note-modal.component';
import { KnownNoteEntityTypes } from '../../../ui/components/higher-order/add-note-action/add-note.hook';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { CompleteRegistrationModal } from '../actions/complete-registration/complete-registration-action.component';
import { CourierDetailsActions } from './actions/courier-details.actions';
import { EditApprovalModal } from './actions/edit-approval/edit-approval-action.component';
import { EditCourierModal } from './actions/edit-courier/edit-courier-action.component';
import { MoveCourierModal } from './actions/move-courier/move-courier-action.component';

/** The decision each entry point opens on; the actions menu picks none. */
const APPROVAL_PRESELECTIONS: Partial<Record<CourierDetailsActions, string>> = {
  [CourierDetailsActions.EDIT_APPROVAL_APPROVE]: KnownCourierApprovalStatus.Approved,
  [CourierDetailsActions.EDIT_APPROVAL_REJECT]: KnownCourierApprovalStatus.Rejected
};

export type CourierActions = {
  currentAction: CourierDetailsActions | null;
  setAction(action: CourierDetailsActions | null): void;
};

export function CourierDetailsActionsObserver({
  actions,
  courier,
  projectID
}: {
  actions: CourierActions;
  courier: CourierDto;
  projectID: string;
}) {
  switch (actions.currentAction) {
    case CourierDetailsActions.COMPLETE_REGISTRATION:
      return (
        <CompleteRegistrationModal
          courier={courier}
          onSuccess={() => actions.setAction(CourierDetailsActions.COMPLETE_REGISTRATION_SUCCESS)}
          onAbort={() => actions.setAction(null)}
        />
      );
    case CourierDetailsActions.COMPLETE_REGISTRATION_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          Registration completed. The courier is now pending approval
        </Snackbar>
      );
    case CourierDetailsActions.EDIT_COURIER:
      return (
        <EditCourierModal
          courier={courier}
          projectID={projectID}
          onSuccess={() => actions.setAction(CourierDetailsActions.EDIT_COURIER_SUCCESS)}
          onAbort={() => actions.setAction(null)}
        />
      );
    case CourierDetailsActions.EDIT_COURIER_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          The courier details have been updated
        </Snackbar>
      );
    case CourierDetailsActions.EDIT_APPROVAL:
    case CourierDetailsActions.EDIT_APPROVAL_APPROVE:
    case CourierDetailsActions.EDIT_APPROVAL_REJECT:
      return (
        <EditApprovalModal
          courier={courier}
          // From the badge, the decision picked there; from the actions menu, what they hold.
          preselected={APPROVAL_PRESELECTIONS[actions.currentAction]}
          onSuccess={() => actions.setAction(CourierDetailsActions.EDIT_APPROVAL_SUCCESS)}
          onAbort={() => actions.setAction(null)}
        />
      );
    case CourierDetailsActions.EDIT_APPROVAL_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          The courier's approval has been updated
        </Snackbar>
      );
    case CourierDetailsActions.MOVE_COURIER:
      return (
        <MoveCourierModal
          courier={courier}
          projectID={projectID}
          onSuccess={() => actions.setAction(CourierDetailsActions.MOVE_COURIER_SUCCESS)}
          onAbort={() => actions.setAction(null)}
        />
      );
    case CourierDetailsActions.MOVE_COURIER_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          The courier has been moved
        </Snackbar>
      );
    case CourierDetailsActions.STATUS_CHANGE_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          The courier's status has been changed
        </Snackbar>
      );
    case CourierDetailsActions.STATUS_CHANGE_ERROR:
      return (
        <Snackbar severity="error" onClose={() => actions.setAction(null)}>
          There was an error updating the courier
        </Snackbar>
      );
    case CourierDetailsActions.ADD_NOTE:
      return (
        <AddNoteActionModal
          id={courier.id}
          projectID={projectID}
          onSuccess={() => actions.setAction(CourierDetailsActions.ADD_NOTE_SUCCESS)}
          onAbort={() => actions.setAction(null)}
          entityType={KnownNoteEntityTypes.Courier}
        />
      );
    case CourierDetailsActions.ADD_NOTE_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => actions.setAction(null)}>
          Note added
        </Snackbar>
      );
    default:
      return null;
  }
}
